import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { API, http, formatDate, imgSrc } from "@/lib/api";
import { MaskLine, Reveal, EASE } from "@/components/site/Reveal";
import { scrollTop } from "@/lib/lenis";

const NotFound = () => (
  <div className="max-w-3xl mx-auto px-5 py-32 text-center" data-testid="post-not-found">
    <p className="font-display text-3xl text-[#221F1B]">This article could not be found.</p>
    <Link to="/blog" className="mt-8 inline-block link-underline text-[#16302B]">Back to all insights</Link>
  </div>
);

const mdComponents = {
  img: ({ src, alt }) => (
    <figure className="my-10">
      <img src={imgSrc(src)} alt={alt || ""} loading="lazy" className="w-full h-auto" />
      {alt && <figcaption className="mt-3 text-[13px] text-[#5C564E] font-body not-italic">{alt}</figcaption>}
    </figure>
  ),
  p: ({ node, children }) => (node?.children?.some((c) => c.tagName === "img") ? <div>{children}</div> : <p>{children}</p>),
};

const Cover = ({ post }) => (
  <div className="max-w-[1180px] mx-auto px-5 sm:px-8 mt-14">
    <motion.div
      className="relative overflow-hidden aspect-[16/10] sm:aspect-[21/9] bg-[#EFE9DC]"
      initial={{ clipPath: "inset(12% 6% 12% 6%)" }}
      animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
      transition={{ duration: 1.4, delay: 0.3, ease: EASE }}
    >
      <motion.img
        src={imgSrc(post.cover_image)}
        alt={post.title}
        className="absolute inset-0 h-full w-full object-cover"
        initial={{ scale: 1.15 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.8, delay: 0.3, ease: EASE }}
        data-testid="post-cover-image"
      />
    </motion.div>
  </div>
);

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined);
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    scrollTop();
    if (!API) {
      setPost(null);
      return undefined;
    }
    let alive = true;
    setPost(undefined);
    http.get(`/posts/${slug}`).then((r) => {
      if (!alive) return;
      setPost(r?.data || null);
      document.title = `${r?.data?.title || "Insight"} — Scale Solutions by Sadiya`;
    }).catch(() => alive && setPost(null));
    return () => { alive = false; };
  }, [slug]);

  if (post === undefined) return <main className="min-h-screen bg-[#F6F2E8] pt-40 text-center text-[#5C564E]" data-testid="post-loading">Loading…</main>;
  if (post === null) return <main className="min-h-screen bg-[#F6F2E8] pt-[72px]"><NotFound /></main>;

  return (
    <main className="bg-[#F6F2E8] min-h-screen pt-[72px]" data-testid="blog-post-page">
      <motion.div style={{ scaleX: scrollYProgress }} className="fixed top-[72px] left-0 right-0 h-[2px] bg-[#B8874A] origin-left z-40" />
      <article className="max-w-3xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24">
        <Link to="/blog" className="inline-flex items-center gap-2 text-[13px] text-[#5C564E] hover:text-[#16302B] transition-colors" data-testid="post-back-link">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> All insights
        </Link>
        <div className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-[#5C564E]">
          <span className="eyebrow">{post.category}</span>
          <span className="h-3 w-px bg-[#D8CFBC]" />
          <span>{formatDate(post.created_at)}</span>
          <span className="h-3 w-px bg-[#D8CFBC]" />
          <span>{post.reading_minutes} min read</span>
        </div>
        <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.1] tracking-[-0.015em] text-[#221F1B]" data-testid="post-title">
          <MaskLine>{post.title}</MaskLine>
        </h1>
        {post.excerpt && <Reveal as="p" className="mt-8 font-display italic text-xl sm:text-2xl leading-relaxed text-[#16302B]">{post.excerpt}</Reveal>}
      </article>
      {post.cover_image && <Cover post={post} />}
      <article className="max-w-3xl mx-auto px-5 sm:px-8 pt-12 pb-24">
        {!post.cover_image && <div className="h-px bg-[#D8CFBC]" />}
        <div className="prose-ss mt-12" data-testid="post-body">
          <ReactMarkdown components={mdComponents}>{post.body || ""}</ReactMarkdown>
        </div>
        <div className="mt-20 bg-[#16302B] text-white p-8 sm:p-12 grain relative overflow-hidden" data-testid="post-cta">
          <p className="eyebrow">Next step</p>
          <p className="font-display text-3xl sm:text-4xl mt-4 leading-tight">Ready to move from chaos to <span className="italic text-[#B8874A]">clarity?</span></p>
          <Link to="/#contact" className="btn-brass on-dark group mt-8 inline-flex items-center gap-3 px-7 py-4 text-sm font-medium" data-testid="post-cta-book-button">
            Book a Discovery Session <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.5} />
          </Link>
        </div>
      </article>
    </main>
  );
}
